import React, { useState, useEffect, useCallback, useRef } from "react";
import { useAuth } from "../contexts/AuthContext";
import {
  FaEye,
  FaEdit,
  FaTrash,
  FaPlus,
  FaShareAlt,
  FaFilter,
  FaSpinner,
} from "react-icons/fa";
import { hasPermission, ROLES, FEATURES } from "../lib/rolesConfig";
import MultiActionScrollButton from "../components/MultiActionScrollButton";

const API_BASE_URL = `https://asia-southeast1-${process.env.REACT_APP_FIREBASE_PROJECT_ID}.cloudfunctions.net/api`;

// Helper to normalize Firestore Timestamps or millis to a JS Date
function toJSDate(rawTs) {
  if (!rawTs) return null;
  // Firestore Timestamps have a .toDate() method
  if (typeof rawTs.toDate === "function") {
    return rawTs.toDate();
  }
  // Otherwise, assume it's a number (milliseconds)
  return new Date(rawTs);
}

const REPORT_TYPES = [
  "Blood Test",
  "Urinalysis",
  "X-ray",
  "MRI",
  "CT Scan",
  "Ultrasound",
  "Other",
];

const initialFormState = {
  reportType: "",
  description: "",
  reportDate: "",
  file: null,
};

const LabReportsPage = () => {
  const { currentUser, activeCareProfile, userRole } = useAuth();
  const [reports, setReports] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [form, setForm] = useState(initialFormState);
  const [search, setSearch] = useState("");
  const [filterType, setFilterType] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [sortOrder, setSortOrder] = useState("desc");
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewType, setPreviewType] = useState(null);
  const [showUploadForm, setShowUploadForm] = useState(false);
  const [editingReportId, setEditingReportId] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [currentFileName, setCurrentFileName] = useState("");
  const cardRefs = useRef({});
  const [isGeneratingPreview, setIsGeneratingPreview] = useState(false);
  const [isSharing, setIsSharing] = useState(false);

  // Pagination State
  const [visibleRecordsCount, setVisibleRecordsCount] = useState(5);

  const canWrite =
    activeCareProfile && userRole
      ? hasPermission(
          userRole[activeCareProfile.id] || ROLES.OWNER,
          FEATURES.LAB_REPORTS,
          "write",
        )
      : false;
  const canDelete =
    activeCareProfile && userRole
      ? hasPermission(
          userRole[activeCareProfile.id] || ROLES.OWNER,
          FEATURES.LAB_REPORTS,
          "delete",
        )
      : false;
  const canShare =
    activeCareProfile && userRole
      ? hasPermission(
          userRole[activeCareProfile.id] || ROLES.OWNER,
          FEATURES.LAB_REPORTS,
          "share",
        )
      : false;

  const filteredAndSortedReports = React.useMemo(() => {
    let data = [...reports];
    // Filtering
    if (filterType) data = data.filter((r) => r.reportType === filterType);
    if (dateFrom)
      data = data.filter(
        (r) => r.reportDate && new Date(r.reportDate) >= new Date(dateFrom),
      );
    if (dateTo)
      data = data.filter(
        (r) => r.reportDate && new Date(r.reportDate) <= new Date(dateTo),
      );
    if (search) {
      const s = search.toLowerCase();
      data = data.filter(
        (r) =>
          (r.description && r.description.toLowerCase().includes(s)) ||
          (r.fileName && r.fileName.toLowerCase().includes(s)) ||
          (r.reportType && r.reportType.toLowerCase().includes(s)),
      );
    }
    // Sorting
    data.sort((a, b) => {
      const dateA =
        toJSDate(a.reportDate) ||
        toJSDate(a.lastModifiedAt) ||
        toJSDate(a.createdAt) ||
        0;
      const dateB =
        toJSDate(b.reportDate) ||
        toJSDate(b.lastModifiedAt) ||
        toJSDate(b.createdAt) ||
        0;

      const timeA = dateA ? dateA.getTime() : 0;
      const timeB = dateB ? dateB.getTime() : 0;

      return sortOrder === "asc" ? timeA - timeB : timeB - timeA;
    });
    return data;
  }, [reports, filterType, dateFrom, dateTo, search, sortOrder]);

  // Fetch lab reports
  const fetchReports = useCallback(async () => {
    if (!currentUser || !activeCareProfile) {
      setReports([]);
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    try {
      const idToken = await currentUser.getIdToken();
      const response = await fetch(
        `${API_BASE_URL}/lab-reports/${activeCareProfile.id}`,
        {
          headers: { Authorization: `Bearer ${idToken}` },
        },
      );

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || "Failed to fetch lab reports");
      }

      const data = await response.json();
      // Data is now pre-formatted by the backend, no need for frontend conversion.
      setReports(data);
    } catch (error) {
      console.error("Error fetching lab reports:", error);
      alert(`Failed to load lab reports: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  }, [currentUser, activeCareProfile]);

  useEffect(() => {
    fetchReports();
  }, [fetchReports]);

  const handleLoadMore = () => {
    setVisibleRecordsCount((prev) => prev + 5);
  };

  useEffect(() => {
    setVisibleRecordsCount(5);
  }, [filterType, dateFrom, dateTo, search, sortOrder]);

  // Handle form input
  const handleFormChange = (e) => {
    const { name, value, files } = e.target;
    if (name === "file" && files.length > 0) {
      const file = files[0];
      const reader = new FileReader();
      reader.onload = (e) => {
        // The result includes the base64 prefix 'data:...,' which we need to remove
        const base64 = e.target.result.split(",")[1];
        setForm((f) => ({
          ...f,
          file: {
            base64: base64,
            name: file.name,
            type: file.type,
          },
        }));
      };
      reader.readAsDataURL(file);
    } else {
      setForm((f) => ({ ...f, [name]: files ? files[0] : value }));
    }
  };

  // Edit handler
  const handleEdit = (report) => {
    if (!canWrite)
      return alert("You don't have permission to edit lab reports.");
    setEditingReportId(report.id);
    setCurrentFileName(report.fileName || "");
    setShowUploadForm(true);
    setForm({
      reportType: report.reportType,
      description: report.description,
      reportDate: report.reportDate,
      file: null, // Reset file input
    });
    setTimeout(() => {
      const formElement = document.getElementById("lab-report-form");
      if (formElement) {
        formElement.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }, 100);
  };

  // Upload/Update handler
  const handleUpload = async (e) => {
    e.preventDefault();
    if (!canWrite)
      return alert("You don't have permission to create or edit lab reports.");

    if (!currentUser || !activeCareProfile) {
      alert("You must be logged in and have an active profile selected.");
      return;
    }
    setUploading(true);

    try {
      const idToken = await currentUser.getIdToken();
      let url;
      let payload;

      if (editingReportId) {
        url = `${API_BASE_URL}/lab-reports/update`;
        payload = {
          reportId: editingReportId,
          careProfileId: activeCareProfile.id,
          reportType: form.reportType,
          description: form.description,
          reportDate: form.reportDate,
        };
        if (form.file) {
          payload.fileData = form.file;
        }
      } else {
        if (!form.reportType || !form.reportDate || !form.file) {
          alert("Please fill in all required fields, including the file.");
          setUploading(false);
          return;
        }
        url = `${API_BASE_URL}/lab-reports/create`;
        payload = {
          careProfileId: activeCareProfile.id,
          reportType: form.reportType,
          description: form.description,
          reportDate: form.reportDate,
          fileData: form.file,
        };
      }

      const response = await fetch(url, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${idToken}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      const result = await response.json();

      if (!response.ok || !result.success) {
        throw new Error(
          result.message ||
            `Failed to ${editingReportId ? "update" : "create"} lab report.`,
        );
      }

      // Reset form and state
      setForm(initialFormState);
      setShowUploadForm(false);
      setEditingReportId(null);
      setCurrentFileName("");
      fetchReports(); // Refresh the list
    } catch (err) {
      alert(
        `Failed to ${editingReportId ? "update" : "upload"} lab report. ${err.message}`,
      );
      console.error(err);
    } finally {
      setUploading(false);
    }
  };

  // Cancel upload handler
  const handleCancelEdit = () => {
    const idToScrollTo = editingReportId;
    setShowUploadForm(false);
    setEditingReportId(null);
    setCurrentFileName("");
    setForm(initialFormState);

    if (idToScrollTo && cardRefs.current[idToScrollTo]) {
      setTimeout(() => {
        cardRefs.current[idToScrollTo].scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      }, 100);
    }
  };

  // Delete handler
  const handleDelete = async (report) => {
    if (!canDelete)
      return alert("You don't have permission to delete lab reports.");
    if (
      window.confirm(
        `Are you sure you want to delete the report: ${report.fileName || report.description}?`,
      )
    ) {
      try {
        const idToken = await currentUser.getIdToken();
        const response = await fetch(`${API_BASE_URL}/lab-reports/delete`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${idToken}`,
          },
          body: JSON.stringify({
            reportId: report.id,
            careProfileId: activeCareProfile.id,
          }),
        });

        const result = await response.json();

        if (!response.ok || !result.success) {
          throw new Error(result.message || "Failed to delete lab report.");
        }

        alert("Lab report deleted successfully.");
        fetchReports(); // Refresh the list
      } catch (err) {
        alert(`Error deleting report: ${err.message}`);
        console.error("Delete error:", err);
      }
    }
  };

  // Preview handler
  const handlePreview = async (report) => {
    setIsGeneratingPreview(true);
    setPreviewUrl(null);

    const path = report.filePath;
    if (!path) {
      alert("Could not preview file: No file path found for this report.");
      setIsGeneratingPreview(false);
      return;
    }

    try {
      const idToken = await currentUser.getIdToken();
      const response = await fetch(
        `${API_BASE_URL}/lab-reports/generate-read-url`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${idToken}`,
          },
          body: JSON.stringify({
            filePath: path,
            careProfileId: activeCareProfile.id,
          }),
        },
      );

      const result = await response.json();

      if (!response.ok || !result.success) {
        throw new Error(result.message || "Could not generate preview link.");
      }

      setPreviewUrl(result.url);
      // Determine file type for rendering (simple check)
      if (report.fileName.toLowerCase().endsWith(".pdf")) {
        setPreviewType("pdf");
      } else if (
        [".png", ".jpg", ".jpeg", ".gif"].some((ext) =>
          report.fileName.toLowerCase().endsWith(ext),
        )
      ) {
        setPreviewType("image");
      } else {
        setPreviewType("other"); // Fallback for other file types
      }
    } catch (err) {
      alert(`Could not preview file: ${err.message}`);
      console.error("Preview error:", err);
    } finally {
      setIsGeneratingPreview(false);
    }
  };

  const closePreview = () => setPreviewUrl(null);

  // Share handler
  const handleShare = async (report) => {
    if (!canShare) {
      alert("You don't have permission to share this lab report.");
      return;
    }

    const path = report.filePath;
    if (!path) {
      alert("Could not share file: No file path found for this report.");
      return;
    }

    setIsSharing(true);
    try {
      const idToken = await currentUser.getIdToken();
      const response = await fetch(
        `${API_BASE_URL}/lab-reports/generate-read-url`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${idToken}`,
          },
          body: JSON.stringify({
            filePath: path,
            careProfileId: activeCareProfile.id,
          }),
        },
      );

      const result = await response.json();
      if (!response.ok || !result.success) {
        throw new Error(result.message || "Could not generate share link.");
      }

      const shareUrl = result.url;

      // Try native Web Share API first
      if (navigator.share) {
        await navigator.share({
          title: report.reportType || "Lab Report",
          text: report.description || "Lab report exported from MILA Care",
          url: shareUrl,
        });
      } else {
        // Fallback: copy to clipboard
        await navigator.clipboard.writeText(shareUrl);
        alert("Share link copied to clipboard!");
      }
    } catch (err) {
      alert(`Could not share file: ${err.message}`);
      console.error("Share error:", err);
    } finally {
      setIsSharing(false);
    }
  };

  return (
    <div className="container mx-auto min-h-screen bg-gray-50 p-4 sm:p-6">
      <MultiActionScrollButton scrollElementId="main-content" />
      <h1 className="text-3xl font-bold text-gray-800 mb-6">
        Health Documents
      </h1>

      {!activeCareProfile ? (
        <div className="text-center py-10">
          <p>Loading...</p>
        </div>
      ) : (
        <>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-6">
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
              <button
                onClick={() => setShowFilters(!showFilters)}
                className="min-h-11 px-4 py-2 bg-gray-200 text-gray-700 font-semibold rounded-lg shadow-sm hover:bg-gray-300 transition focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
              >
                <FaFilter className="inline mr-2" />
                {showFilters ? "Hide" : "Show"} Filters
              </button>
              {canWrite && (
                <button
                  onClick={() => {
                    setEditingReportId(null);
                    setForm(initialFormState);
                    setShowUploadForm(true);
                    setTimeout(() => {
                      const formElement =
                        document.getElementById("lab-report-form");
                      if (formElement)
                        formElement.scrollIntoView({
                          behavior: "smooth",
                          block: "start",
                        });
                    }, 100);
                  }}
                  className="min-h-11 px-4 py-2 bg-blue-600 text-white font-semibold rounded-lg shadow-md hover:bg-blue-700 transition focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
                >
                  <FaPlus className="inline mr-2" /> Upload New
                </button>
              )}
            </div>
          </div>

          {/* Upload Form */}
          {showUploadForm && (
            <div
              id="lab-report-form"
              className="bg-white p-6 mb-8 rounded-lg shadow-lg border-l-4 border-blue-500"
            >
              <h3 className="text-2xl font-semibold mb-4 text-gray-700">
                {editingReportId
                  ? "Edit Health Document"
                  : "Upload New Health Document"}
              </h3>
              <form onSubmit={handleUpload} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <select
                    name="reportType"
                    value={form.reportType}
                    onChange={handleFormChange}
                    required
                    className="w-full p-2 border rounded"
                  >
                    <option value="">Select Document Type</option>
                    {REPORT_TYPES.map((type) => (
                      <option key={type} value={type}>
                        {type}
                      </option>
                    ))}
                  </select>
                  <input
                    type="date"
                    name="reportDate"
                    value={form.reportDate}
                    onChange={handleFormChange}
                    required
                    className="w-full p-2 border rounded"
                  />
                </div>
                <textarea
                  name="description"
                  value={form.description}
                  onChange={handleFormChange}
                  placeholder="Description or notes"
                  className="w-full p-2 border rounded"
                />
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    {editingReportId
                      ? "Upload New File (Optional)"
                      : "Upload File"}
                  </label>
                  <input
                    type="file"
                    name="file"
                    onChange={handleFormChange}
                    className="w-full p-2 border rounded mt-1"
                  />
                  {editingReportId && currentFileName && (
                    <p className="text-sm text-gray-500 mt-1">
                      Current file:{" "}
                      <span className="font-medium">{currentFileName}</span>
                    </p>
                  )}
                </div>
                <div className="flex justify-end gap-4">
                  <button
                    type="button"
                    onClick={handleCancelEdit}
                    className="px-4 py-2 bg-gray-200 rounded"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={uploading}
                    className="px-4 py-2 bg-blue-600 text-white rounded disabled:bg-blue-300"
                  >
                    {uploading
                      ? "Saving..."
                      : editingReportId
                        ? "Save Changes"
                        : "Upload"}
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* Filters Section */}
          {showFilters && (
            <div className="bg-white p-4 mb-6 rounded-lg shadow-md border">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <input
                  type="text"
                  placeholder="Search..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="w-full p-2 border rounded"
                />
                <select
                  value={filterType}
                  onChange={(e) => setFilterType(e.target.value)}
                  className="w-full p-2 border rounded"
                >
                  <option value="">All Types</option>
                  {REPORT_TYPES.map((type) => (
                    <option key={type} value={type}>
                      {type}
                    </option>
                  ))}
                </select>
                <input
                  type="date"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                  className="w-full p-2 border rounded"
                />
                <input
                  type="date"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                  className="w-full p-2 border rounded"
                />
              </div>
              <div className="flex flex-wrap items-center justify-between mt-4 gap-4">
                <div className="flex items-center space-x-2">
                  <span className="text-sm font-medium text-gray-600">
                    Sort by Date:
                  </span>
                  <button
                    onClick={() => setSortOrder("desc")}
                    className={`px-3 py-1 text-sm rounded ${sortOrder === "desc" ? "bg-blue-500 text-white" : "bg-gray-200"}`}
                  >
                    Newest
                  </button>
                  <button
                    onClick={() => setSortOrder("asc")}
                    className={`px-3 py-1 text-sm rounded ${sortOrder === "asc" ? "bg-blue-500 text-white" : "bg-gray-200"}`}
                  >
                    Oldest
                  </button>
                </div>
                <button
                  onClick={() => {
                    setFilterType("");
                    setDateFrom("");
                    setDateTo("");
                    setSearch("");
                  }}
                  className="px-4 py-1 text-sm bg-gray-600 text-white rounded-lg hover:bg-gray-700"
                >
                  Clear Filters
                </button>
              </div>
            </div>
          )}

          {/* Reports List */}
          <div className="bg-white p-6 rounded-lg shadow-lg">
            <h3 className="text-2xl font-semibold mb-4 text-gray-700">
              History
            </h3>
            {isLoading ? (
              <div className="text-center p-4">Loading reports...</div>
            ) : filteredAndSortedReports.length === 0 ? (
              <div className="text-center p-4 text-gray-500 italic">
                No health documents found for the selected criteria.
              </div>
            ) : (
              <>
                <div className="space-y-4">
                  {filteredAndSortedReports
                    .slice(0, visibleRecordsCount)
                    .map((report) => (
                      <div
                        key={report.id}
                        ref={(el) => (cardRefs.current[report.id] = el)}
                        className="bg-gray-50 p-4 rounded-lg shadow-sm border border-gray-200 transition-shadow hover:shadow-md relative"
                      >
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-x-4 gap-y-2">
                          {/* Left Column: Details */}
                          <div className="md:col-span-2">
                            <p className="font-bold text-lg text-gray-800">
                              {report.reportType}
                            </p>
                            <p className="text-sm text-gray-600 sm:pr-20">
                              {report.description || "No description provided."}
                            </p>
                            <p className="text-sm text-gray-500 mt-1">
                              Report Date:{" "}
                              <span className="font-medium">
                                {toJSDate(
                                  report.reportDate,
                                )?.toLocaleDateString() || "N/A"}
                              </span>
                            </p>
                            <p className="text-xs text-gray-400 mt-1">
                              File: {report.fileName}
                            </p>
                            <div className="text-xs text-gray-500 mt-1">
                              Uploaded by: {report.createdBy?.name || "Unknown"}{" "}
                              on{" "}
                              {toJSDate(
                                report.createdAt,
                              )?.toLocaleDateString() || "N/A"}
                            </div>
                            {report.lastModifiedAt &&
                              report.lastModifiedBy?.uid !== report.userId && (
                                <div className="text-xs text-gray-400 italic mt-1">
                                  Last modified by:{" "}
                                  {report.lastModifiedBy?.name || "Unknown"} on{" "}
                                  {toJSDate(
                                    report.lastModifiedAt,
                                  )?.toLocaleDateString() || "N/A"}
                                </div>
                              )}
                          </div>
                          {/* Right Column: Actions */}
                          {/* Mobile Actions */}
                          <div className="flex items-center justify-start md:hidden space-x-2 mt-2 md:mt-0">
                            <button
                              onClick={() => handlePreview(report)}
                              disabled={isGeneratingPreview}
                              className="p-2 text-blue-600"
                              title="View Report"
                            >
                              {isGeneratingPreview ? (
                                <FaSpinner className="animate-spin" size={18} />
                              ) : (
                                <FaEye size={18} />
                              )}
                            </button>
                            <button
                              onClick={() => handleShare(report)}
                              disabled={isSharing}
                              className="p-2 text-green-600"
                              title="Share Report"
                            >
                              {isSharing ? (
                                <FaSpinner className="animate-spin" size={16} />
                              ) : (
                                <FaShareAlt size={16} />
                              )}
                            </button>
                            {canWrite && (
                              <button
                                onClick={() => handleEdit(report)}
                                className="p-2 text-yellow-600"
                                title="Edit Details"
                              >
                                <FaEdit size={16} />
                              </button>
                            )}
                            {canDelete && (
                              <button
                                onClick={() => handleDelete(report)}
                                className="p-2 text-red-600"
                                title="Delete Report"
                              >
                                <FaTrash size={16} />
                              </button>
                            )}
                          </div>
                        </div>
                        {/* Tablet/Desktop Actions */}
                        <div className="hidden md:flex items-center justify-end space-x-2 absolute top-4 right-4">
                          <button
                            onClick={() => handlePreview(report)}
                            disabled={isGeneratingPreview}
                            className="p-2 text-blue-600 hover:text-blue-800"
                            title="View Report"
                          >
                            {isGeneratingPreview ? (
                              <FaSpinner className="animate-spin" size={18} />
                            ) : (
                              <FaEye size={18} />
                            )}
                          </button>
                          <button
                            onClick={() => handleShare(report)}
                            disabled={isSharing}
                            className="p-2 text-green-600 hover:text-green-800"
                            title="Share Report"
                          >
                            {isSharing ? (
                              <FaSpinner className="animate-spin" size={16} />
                            ) : (
                              <FaShareAlt size={16} />
                            )}
                          </button>
                          {canWrite && (
                            <button
                              onClick={() => handleEdit(report)}
                              className="p-2 text-yellow-600 hover:text-yellow-800"
                              title="Edit Details"
                            >
                              <FaEdit size={16} />
                            </button>
                          )}
                          {canDelete && (
                            <button
                              onClick={() => handleDelete(report)}
                              className="p-2 text-red-600 hover:text-red-800"
                              title="Delete Report"
                            >
                              <FaTrash size={16} />
                            </button>
                          )}
                        </div>

                        {/* Audit Trail */}
                        <div className="text-xs text-gray-500 italic mt-2 pt-2 border-t border-gray-200">
                          {report.lastModifiedBy
                            ? report.lastModifiedBy.uid !==
                              activeCareProfile.userId
                              ? `Last updated by ${report.lastModifiedBy.name} on ${toJSDate(report.lastModifiedAt || report.createdAt)?.toLocaleString() || "N/A"}`
                              : `Updated on ${toJSDate(report.lastModifiedAt || report.createdAt)?.toLocaleString() || "N/A"}`
                            : `Uploaded on ${toJSDate(report.createdAt || { toDate: () => new Date(report.reportDate) })?.toLocaleString() || "N/A"}`}
                        </div>
                      </div>
                    ))}
                </div>

                {/* Load More Button */}
                {visibleRecordsCount < filteredAndSortedReports.length && (
                  <div className="mt-8 flex justify-center">
                    <button
                      onClick={handleLoadMore}
                      className="px-6 py-2 bg-blue-500 text-white font-semibold rounded-lg shadow-md hover:bg-blue-600 transition-all"
                    >
                      Load More
                    </button>
                  </div>
                )}
              </>
            )}
          </div>

          {/* Preview Modal */}
          {previewUrl && (
            <div
              className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50"
              onClick={closePreview}
            >
              <div
                className="bg-white p-4 rounded-lg max-w-4xl max-h-[90vh] overflow-auto"
                onClick={(e) => e.stopPropagation()}
              >
                {previewType === "pdf" ? (
                  <iframe
                    src={previewUrl}
                    className="w-[80vw] h-[85vh]"
                    title="Report Preview"
                  ></iframe>
                ) : (
                  <img
                    src={previewUrl}
                    alt="Report Preview"
                    className="max-w-full max-h-full"
                  />
                )}
                <button
                  onClick={closePreview}
                  className="absolute top-4 right-4 text-white text-2xl"
                >
                  &times;
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default LabReportsPage;
